import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Phone, Building2, User, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sendDemoRequestEmail } from '@/services/demoRequestService';
import CustomAlert from '../ui/CustomAlert';

function BookDemoModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    organisationName: '',
    mobileNumber: '',
    message: '',
  })
  const [alert, setAlert] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      resetForm()
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [open])

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      organisationName: '',
      mobileNumber: '',
      message: '',
    })
    setAlert(null)
    setLoading(false)
  }

  if (!open || typeof document === 'undefined') return null

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async () => {
    setAlert(null)

    // Validation
    if (!form.name.trim()) {
      setAlert({
        type: 'error',
        title: t('demo_form_alert_validation_title'),
        message: t('demo_form_error_name')
      })
      return
    }

    if (!form.email.trim()) {
      setAlert({
        type: 'error',
        title: t('demo_form_alert_validation_title'),
        message: t('demo_form_error_email')
      })
      return
    }

    if (!validateEmail(form.email)) {
      setAlert({
        type: 'error',
        title: t('demo_form_alert_validation_title'),
        message: t('demo_form_error_email_invalid')
      })
      return
    }

    if (!form.message.trim()) {
      setAlert({
        type: 'error',
        title: t('demo_form_alert_validation_title'),
        message: t('demo_form_error_message')
      })
      return
    }

    setLoading(true)

    try {
      // Send demo request email via API
      const result = await sendDemoRequestEmail(form)

      if (result.success) {
        setAlert({
          type: 'success',
          title: t('demo_form_alert_success_title'),
          message: t('demo_form_status_success')
        })
        setTimeout(() => {
          resetForm()
          onClose?.()
          onSuccess?.()
        }, 1500)
      } else {
        setAlert({
          type: 'error',
          title: t('demo_form_alert_error_title'),
          message: result.message || t('demo_form_error_failed_submit')
        })
        setLoading(false)
      }
    } catch (error) {
      console.error('Demo request submission failed:', error)
      setAlert({
        type: 'error',
        title: t('demo_form_alert_error_title'),
        message: t('demo_form_error_failed_submit')
      })
      setLoading(false)
    }
  }

  const gradientBg = 'bg-gradient-to-r from-[#7096F3] via-[#4181F3] via-40% to-[#C08EDB]'

  return createPortal(
    <div
      className="fixed inset-0 z-[9995] flex items-center justify-center bg-slate-950/70 px-3 py-3 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        {/* Header */}
        <header className={`relative z-10 flex items-center justify-between px-8 py-3 text-white ${gradientBg}`}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 -mb-0.5">Medica</p>
            <p className="text-xl font-bold">{t('demo_modal_title')}</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label={t('demo_modal_close_aria')}
          >
            <X size={18} />
          </button>
        </header>

        {/* Content */}
        <div className="relative z-10 px-8 pb-6 pt-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-base text-slate-600">{t('demo_modal_subtitle')}</p>
            </div>

            {/* Form Fields - Two Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-3">
                {/* Name Field */}
                <div>
                  <label className="text-sm font-semibold text-slate-700">{t('demo_form_name_label')} <span className="text-red-500">*</span></label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-10 py-3 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder={t('demo_form_name_placeholder')}
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <label className="text-sm font-semibold text-slate-700">{t('demo_form_email_label')} <span className="text-red-500">*</span></label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-10 py-3 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder={t('demo_form_email_placeholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-3">
                {/* Mobile Number Field (Optional) */}
                <div>
                  <label className="text-sm font-semibold text-slate-700">{t('demo_form_mobile_label')} <span className="text-slate-500"></span></label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="tel"
                      value={form.mobileNumber}
                      onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-10 py-3 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder={t('demo_form_mobile_placeholder')}
                      inputMode="numeric"
                    />
                  </div>
                </div>

                {/* Organisation Name Field (Optional) */}
                <div>
                  <label className="text-sm font-semibold text-slate-700">{t('demo_form_organisation_label')} <span className="text-slate-500"></span></label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={form.organisationName}
                      onChange={(e) => setForm({ ...form, organisationName: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-10 py-3 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder={t('demo_form_organisation_placeholder')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Message Field - Full Width */}
            <div>
              <label className="text-sm font-semibold text-slate-700">{t('demo_form_message_label')} <span className="text-red-500">*</span></label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 resize-none"
                placeholder={t('demo_form_message_placeholder')}
                rows={4}
              />
            </div>

            {/* Messages */}
            {alert && (
              <CustomAlert
                type={alert.type}
                title={alert.title}
                message={alert.message}
                onClose={() => setAlert(null)}
                autoClose={alert.type === 'error' ? true : false}
                duration={alert.type === 'error' ? 4000 : 0}
              />
            )}

            {/* Submit Button */}
            <div className="flex flex-col items-center space-y-2 pt-1">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full max-w-xs rounded-lg px-6 py-3 text-base font-semibold text-white shadow-sm transition ${gradientBg} disabled:opacity-60 flex items-center justify-center gap-2 hover:shadow-lg`}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    {t('demo_form_submitting')}
                  </>
                ) : (
                  t('demo_form_submit')
                )}
              </button>

              <p className="text-sm text-slate-500 text-center max-w-md">
                {t('demo_form_footer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default BookDemoModal
